import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { projectInfoSelector } from 'reducer/project/projectInfoReducer'

import { useDataProvider } from 'react-admin'

export interface FeedbackSubmissionData {
  feature_type: feedbackSupportedFeatures
  template_id?: number
  is_positive: boolean | null
  text_content: string
  selected_feedback_options?: string
  org_id?: number
  project_id?: number
  page_url?: string
  feedback_metadata?: Record<string, any>
  screenshot_data?: string | null
  chat_message_id?: number
  feedback_id?: number
}

export type feedbackSupportedFeatures = 'auto_design' | 'ada_chat'

export interface UseFeedbackSubmissionOptions {
  featureType: feedbackSupportedFeatures
  onSuccess?: (skipIsSubmitted: boolean, feedbackId?: number) => void
  onError?: (error: Error) => void
}

export interface UseFeedbackScreenshotSubmissionOptions {
  onSuccess?: (feedbackId: number) => void
  onError?: (error: Error) => void
}

export const useFeedbackScreenshotSubmission = (options: UseFeedbackScreenshotSubmissionOptions) => {
  const dataProvider = useDataProvider()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()
  const translate = useTranslate()
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const submitScreenshot = useCallback(
    async (feedbackId: number, screenshotData: string, silent: boolean = false) => {
      if (isSubmitting) return

      if (isMountedRef.current) {
        setIsSubmitting(true)
      }

      const handleSuccess = (response: any) => {
        try {
          options.onSuccess?.(feedbackId)
        } catch (error) {
          console.error('Error in options.onSuccess:', error)
        }

        if (!isMountedRef.current) {
          return
        }

        if (!silent) {
          notify(translate('Screenshot updated successfully'), 'success')
        }
      }

      const handleFailure = (error: any) => {
        if (!isMountedRef.current) return
        console.error('Error updating screenshot:', error)
        options.onError?.(error as Error)
      }

      try {
        await dataProvider.CUSTOM_PATCH(
          'custom',
          {
            url: 'feedback/update-screenshot/',
            data: {
              feedback_id: feedbackId,
              screenshot_data: screenshotData,
              screenshot_added_at: new Date().toISOString(),
            },
          },
          {
            onSuccess: handleSuccess,
            onFailure: handleFailure,
          }
        )
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Error updating screenshot:', error)
          options.onError?.(error as Error)
        }
      } finally {
        if (isMountedRef.current) {
          setIsSubmitting(false)
        }
      }
    },
    [dataProvider, options, isSubmitting, notify, translate]
  )

  return { submitScreenshot, isSubmittingScreenshot: isSubmitting }
}

export const useFeedbackSubmission = (options: UseFeedbackSubmissionOptions) => {
  const dataProvider = useDataProvider()
  const orgId = useSelector(authSelectors.getOrgId)
  const projectId = useSelector(projectInfoSelector.getProjectId)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()
  const translate = useTranslate()
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const submitFeedback = useCallback(
    async (data: Partial<FeedbackSubmissionData>, silent: boolean, skipIsSubmitted: boolean) => {
      if (isSubmitting) return

      if (isMountedRef.current) {
        setIsSubmitting(true)
      }

      const feedbackData: FeedbackSubmissionData = {
        feature_type: options.featureType,
        template_id: data.template_id ?? undefined,
        is_positive: data.is_positive ?? null,
        text_content: data.text_content ?? '',
        selected_feedback_options: data.selected_feedback_options ?? '',
        org_id: data.org_id ?? orgId,
        project_id: data.project_id ?? projectId,
        page_url: data.page_url ?? window.location.href,
        feedback_metadata: data.feedback_metadata ?? {},
        screenshot_data: data.screenshot_data ?? '',
        chat_message_id: data.chat_message_id,
        feedback_id: data.feedback_id,
      }

      const handleSuccess = (response: any) => {
        try {
          options.onSuccess?.(skipIsSubmitted, response?.data?.feedback_id)
        } catch (error) {
          console.error('Error in options.onSuccess:', error)
        }

        if (!isMountedRef.current) {
          return
        }
        logAmplitudeEvent('generic_button_clicked', {
          source: `${options.featureType}_feedback`,
          context: 'feedback_submission',
          is_positive: feedbackData.is_positive,
          has_text: !!feedbackData.text_content.trim(),
          has_screenshot: !!feedbackData.screenshot_data,
          org_id: orgId,
          project_id: projectId,
          created: response?.data?.created,
          ...feedbackData.feedback_metadata,
        })

        if (!silent) {
          notify(translate('Feedback submitted') + ' - ' + translate('Thank you'), 'success')
        }
      }

      const handleFailure = (error: any) => {
        if (!isMountedRef.current) return
        console.error('Error submitting feedback:', error)
        options.onError?.(error as Error)
      }

      try {
        await dataProvider.CUSTOM_POST(
          'custom',
          {
            url: 'feedback/',
            data: feedbackData,
          },
          {
            onSuccess: handleSuccess,
            onFailure: handleFailure,
          }
        )
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Error submitting feedback:', error)
          options.onError?.(error as Error)
        }
      } finally {
        if (isMountedRef.current) {
          setIsSubmitting(false)
        }
      }
    },
    [dataProvider, options, orgId, projectId, isSubmitting, notify, translate]
  )

  return { submitFeedback, isSubmitting }
}
